import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { BRAND } from '../../lib/constants';
import styles from './Footer.module.css';

export const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Define valid routes to identify if we are on a 404 page
  const isHome = location.pathname === '/';
  const isPortfolio = location.pathname === '/portfolio';
  const isProjectDetail = location.pathname.startsWith('/portfolio/');
  const isSegmentDetail = location.pathname.startsWith('/segments/');
  const isResearch = location.pathname === '/research';
  const isLegalPage = location.pathname === '/privacy' || location.pathname === '/cookies';
  const isValidRoute = isHome || isPortfolio || isProjectDetail || isSegmentDetail || isResearch || isLegalPage;

  // Show CTA band only on valid subpages (not on Home and not on 404)
  const showCtaBand = !isHome && isValidRoute;

  return (
    <footer className={styles.footer}>
      {/* ── CTA Band ── */}
      {showCtaBand && (
        <section className={styles.ctaBand}>
          <div className={styles.ctaGlow} />
          <div className={styles.ctaGrid} />
          <div className={`container ${styles.ctaInner}`}>
            <h2 className={styles.ctaHeadline}>{t('footer.cta_headline')}</h2>
            <p className={styles.ctaDesc}>{t('footer.cta_desc')}</p>
            <Link to="/#contact" className={styles.ctaButton}>
              {t('footer.cta_button')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </section>
      )}

      {/* ── Main Footer ── */}
      <div className={styles.main}>
        <div className={styles.mainGrid} />
        <div className={`container ${styles.mainInner}`}>
          <div className={styles.columns}>
            {/* Brand */}
            <div className={styles.brandCol}>
              <div className={styles.brandLogo}>
                <img src="/logo.svg" alt={BRAND.name} width={400} height={400} className={styles.logoImg} />
                <span className={styles.logoText}>
                  <span className={styles.logoCyan}>N</span>ex
                  <span className={styles.logoCyan}>T</span>rain
                  <span className={styles.logoCyan}>E</span>
                </span>
              </div>
              <p className={styles.brandDesc}>{t('footer.brand_desc')}</p>
              <div className={styles.badge}>
                <span className={styles.badgeDot} />
                <span className={styles.badgeText}>Spin-off {BRAND.university}</span>
              </div>
            </div>

            {/* Navigation */}
            <div className={styles.linkCol}>
              <h3 className={styles.colTitle}>{t('footer.company_title')}</h3>
              <nav className={styles.linkList}>
                <Link to="/#about" className={styles.link}>{t('nav.about')}</Link>
                <Link to="/#services" className={styles.link}>{t('nav.services')}</Link>
                <Link to="/research" className={styles.link}>{t('nav.research')}</Link>
                <Link to="/portfolio" className={styles.link}>{t('nav.portfolio')}</Link>
                <Link to="/#contact" className={styles.link}>{t('nav.contact')}</Link>
              </nav>
            </div>

            {/* Connect */}
            <div className={styles.linkCol}>
              <h3 className={styles.colTitle}>{t('contact.label')}</h3>
              <div className={styles.linkList}>
                <a href={`mailto:${BRAND.email}`} className={styles.contactLink}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 7l-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
                  </svg>
                  {BRAND.email}
                </a>
                <span className={styles.locationItem}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {BRAND.city}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className={styles.bottom}>
        <div className="container">
          <div className={styles.bottomInner}>
            <span className={styles.copyright}>
              © {new Date().getFullYear()} {BRAND.legal} · {BRAND.vat}
            </span>
            <div className={styles.legal}>
              <Link to="/privacy" className={styles.legalLink}>{t('footer.privacy')}</Link>
              <span className={styles.legalDot}>·</span>
              <Link to="/cookies" className={styles.legalLink}>{t('footer.cookie')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
